import { Component, AfterViewInit, OnDestroy, DestroyRef, ElementRef, ViewChild, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-auth-layout',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './auth-layout.component.html',
  styleUrl: './auth-layout.component.scss'
})
export class AuthLayoutComponent implements AfterViewInit, OnDestroy {
  @ViewChild('slideshow') slideshowEl!: ElementRef;

  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);

  private currentSlide = 0;
  private slideInterval: ReturnType<typeof setInterval> | null = null;
  private slides: HTMLElement[] = [];
  private indicators: HTMLElement[] = [];

  protected readonly redirectSource = signal<string | null>(this.readStoredSource());

  protected readonly redirectAppName = computed<string | null>(() => {
    // Normalize: callers send 'colo', 'siteLocator' / 'sitelocator', etc.
    // Compare lower-cased to tolerate inconsistent casing across apps.
    switch ((this.redirectSource() ?? '').toLowerCase()) {
      case 'colo':
      case 'onlinecolo':
        return 'Online Colo';
      case 'sitelocator':
        return 'Site Locator';
      default:
        return null;
    }
  });

  constructor() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const fromQuery = params.get('source');
        if (fromQuery) {
          this.redirectSource.set(fromQuery);
        } else if (!this.redirectSource()) {
          this.redirectSource.set(this.readStoredSource());
        }
      });
  }

  private readStoredSource(): string | null {
    try {
      return localStorage.getItem('externalRedirectSource');
    } catch {
      return null;
    }
  }

  ngAfterViewInit(): void {
    const container = this.slideshowEl?.nativeElement;
    if (!container) return;

    this.slides = Array.from(container.querySelectorAll('.auth-slide'));
    this.indicators = Array.from(container.parentElement.querySelectorAll('.auth-indicator'));

    this.indicators.forEach((indicator, index) => {
      indicator.addEventListener('click', () => this.showSlide(index));
    });

    this.slideInterval = setInterval(() => {
      this.showSlide((this.currentSlide + 1) % this.slides.length);
    }, 7000);
  }

  ngOnDestroy(): void {
    if (this.slideInterval) {
      clearInterval(this.slideInterval);
    }
  }

  private showSlide(index: number): void {
    this.slides.forEach(s => s.classList.remove('active'));
    this.indicators.forEach(i => i.classList.remove('active'));

    this.slides[index]?.classList.add('active');
    this.indicators[index]?.classList.add('active');
    this.currentSlide = index;
  }
}
